import { useCallback } from 'react';

import { usePico } from '../pico';

// =============================================================================
// TYPES
// =============================================================================

export type AiFeature = 'video_details' | 'chapters' | 'highlights' | 'ask_ai' | 'translate';
export type AiLocation =
  | 'ai_panel'
  | 'inline_title'
  | 'inline_description'
  | 'inline_tags'
  | 'languages_panel';
export type AiSource = 'ai_panel' | 'languages_panel';
export type AiVideoDetailsField = 'title' | 'description' | 'tags' | 'all';
export type AiVideoDetailsSaveAction = 'save' | 'replace';
export type AiAskAiQuestionSource = 'typed' | 'suggested_question' | 'related_question';
export type AiHighlightType = 'recap' | 'moment';
export type AiTranslateProductType = 'subtitles_only' | 'audio_and_subtitles';
export type AiTranslateSubmitAction = 'continue' | 'finish_and_save';
export type AiUpsellFeature =
  | 'video_details'
  | 'chapters'
  | 'highlights'
  | 'ask_ai'
  | 'translate'
  | 'ai';
export type AiPanelCloseReason = 'button' | 'outside_click' | 'route_change';
export type AiLanguagesTrackType = 'subtitles' | 'audio';

export type VideoManagePageDisplayedProps = {
  clipId: string;
  referrerPage: string;
};

export type VideoManageStatusChangedProps = {
  clipId: string;
  newStatus: string;
};

export type VideoManageOverflowActionClickedProps = {
  clipId: string;
  action: string;
};

export type VideoManageAppearanceDisplayedProps = {
  clipId: string;
};

export type VideoManageAppearanceModeSwitchedProps = {
  clipId: string;
  mode: string;
};

export type VideoManageAppearanceBrandingChangedProps = {
  clipId: string;
  field: string;
  mode: string;
};

export type VideoManageAppearanceEngagementToggledProps = {
  clipId: string;
  feature: string;
  newStatus: boolean;
  mode: string;
};

export type VideoManageAppearanceDetailToggledProps = {
  clipId: string;
  field: string;
  newStatus: boolean;
  mode: string;
};

export type VideoManageAppearanceSavedProps = {
  clipId: string;
  mode: string;
};

export type VideoManageAppearanceControlChangedProps = {
  clipId: string;
  field: string;
  mode: string;
};

export type VideoManageLanguagesDisplayedProps = {
  clipId: string;
};

export type VideoManageSubtitlesGenerationStartedProps = {
  clipId: string;
};

export type VideoManageSubtitlesUploadedProps = {
  clipId: string;
};

export type VideoManageLanguagesAddActionClickedProps = {
  clipId: string;
  action: string;
};

export type VideoManageLanguagesOverflowActionClickedProps = {
  clipId: string;
  action: string;
};

export type VideoManageInteractivityDisplayedProps = {
  clipId: string;
};

export type VideoManageInteractivityFeatureOpenedProps = {
  clipId: string;
  feature: string;
};

export type VideoManageInteractivityChapterAddedProps = {
  clipId: string;
};

export type VideoManageInteractivityChapterSavedProps = {
  clipId: string;
};

export type VideoManageInteractivityRegistrationToggledProps = {
  clipId: string;
  newStatus: boolean;
};

export type VideoManageInteractivityRegistrationCustomizeClickedProps = {
  clipId: string;
};

export type VideoManageInteractivityRegistrationManageRegistrantsClickedProps = {
  clipId: string;
};

export type VideoManageInteractivityCardAddedProps = {
  clipId: string;
};

export type VideoManageInteractivityCardImageAddedProps = {
  clipId: string;
};

export type VideoManageInteractivityEndScreenSourceChangedProps = {
  clipId: string;
  source: string;
};

export type VideoManageInteractivityVideoRatingSavedProps = {
  clipId: string;
  ratingType: string;
};

export type VideoManageInteractivityVideoRatingTypeChangedProps = {
  clipId: string;
  ratingType: string;
};

export type VideoManageInteractivityCardSavedProps = {
  clipId: string;
  hasDescription: boolean;
  hasImage: boolean;
};

export type VideoManageInteractivityEndScreenSavedProps = {
  clipId: string;
  source: string;
  videoCount?: number;
};

export type VideoManageEditingActionClickedProps = {
  clipId: string;
  action: string;
};

export type VideoManageGifMakerCreateClickedProps = {
  clipId: string;
};

export type VideoManageGifMakerCreatedProps = {
  clipId: string;
};

export type VideoManageGifMakerDeletedProps = {
  clipId: string;
};

export type VideoManageGifMakerShareActionClickedProps = {
  clipId: string;
  action: string;
};

export type VideoManageVimeoAiPanelDisplayedProps = {
  clipId: string;
  source?: 'sidebar' | 'player';
};

export type VideoManageVimeoAiFeatureClickedProps = {
  clipId: string;
  generationId: string | null;
  feature: AiFeature;
  location: AiLocation;
};

export type VideoManageVimeoAiSettingsClickedProps = {
  clipId: string;
};

export type VideoManageVimeoAiPanelClosedProps = {
  clipId: string;
  closeReason: AiPanelCloseReason;
};

export type VideoManageVimeoAiVideoDetailsShownProps = {
  clipId: string;
  generationId: string | null;
  fieldsGenerated: Array<'title' | 'description' | 'tags'>;
};

export type VideoManageVimeoAiVideoDetailsSavedProps = {
  clipId: string;
  generationId: string | null;
  field: AiVideoDetailsField;
  action: AiVideoDetailsSaveAction;
};

export type VideoManageVimeoAiVideoDetailsCopiedProps = {
  clipId: string;
  generationId: string | null;
  field: AiVideoDetailsField;
};

export type VideoManageVimeoAiChaptersShownProps = {
  clipId: string;
  generationId: string | null;
  chapterCount: number;
};

export type VideoManageVimeoAiChaptersChapterClickedProps = {
  clipId: string;
  generationId: string | null;
  chapterIndex: number;
  timecode: number;
};

export type VideoManageVimeoAiChaptersSavedProps = {
  clipId: string;
  generationId: string | null;
  chapterCount: number;
};

export type VideoManageVimeoAiHighlightsShownProps = {
  clipId: string;
  generationId: string | null;
  highlightCount: number;
};

export type VideoManageVimeoAiHighlightsHighlightClickedProps = {
  clipId: string;
  generationId: string | null;
  highlightType: AiHighlightType;
  highlightTitle: string;
};

export type VideoManageVimeoAiHighlightsEditedProps = {
  clipId: string;
  generationId: string | null;
  highlightTitle: string;
};

export type VideoManageVimeoAiHighlightsSavedProps = {
  clipId: string;
  generationId: string | null;
  highlightTitle: string;
  highlightType: AiHighlightType;
};

export type VideoManageVimeoAiHighlightsSharedProps = {
  clipId: string;
  generationId: string | null;
  distributionType: string;
};

export type VideoManageVimeoAiAskAiShownProps = {
  clipId: string;
  generationId: string | null;
};

export type VideoManageVimeoAiAskAiQuestionAskedProps = {
  clipId: string;
  generationId: string | null;
  question: string;
  source: AiAskAiQuestionSource;
};

export type VideoManageVimeoAiAskAiAnswerShownProps = {
  clipId: string;
  generationId: string | null;
  question: string;
};

export type VideoManageVimeoAiAskAiMomentPlayedProps = {
  clipId: string;
  generationId: string | null;
  question: string;
  timecode: number;
};

export type VideoManageVimeoAiTranslateSettingsShownProps = {
  clipId: string;
  generationId: string | null;
  source: AiSource;
  productSelectionShown: boolean;
};

export type VideoManageVimeoAiTranslateSavedProps = {
  clipId: string;
  generationId: string | null;
  source: AiSource;
  productType: AiTranslateProductType;
  sourceLanguageCode: string | null;
  targetLanguageCodes: string[];
  languageCount: number;
  speakerCount: number | null;
  previewEnabled: boolean;
  submitAction: AiTranslateSubmitAction;
};

export type VideoManageVimeoAiUpsellTriggeredProps = {
  clipId: string;
  feature: AiUpsellFeature;
  upsellName: string;
};

export type VideoManageVimeoAiUpsellImpressionProps = {
  clipId: string;
  feature: AiUpsellFeature;
  upsellName: string;
};

export type VideoManageVimeoAiUpsellContactSalesClickedProps = {
  clipId: string;
  feature: AiUpsellFeature;
};

export type VideoManageLanguagesTrackToggledProps = {
  clipId: string;
  trackType: AiLanguagesTrackType;
  languageCode: string;
  enabled: boolean;
};

export type VideoManageLanguagesTrackEditedProps = {
  clipId: string;
  trackType: AiLanguagesTrackType;
  languageCode: string;
};

export type VideoManageLanguagesTrackRetranscribeClickedProps = {
  clipId: string;
  trackType: AiLanguagesTrackType;
  languageCode: string;
};

export type VideoManageInteractivityRegistrationFormTabSwitchedProps = {
  clipId: string;
  tab: string;
};

export type VideoManageInteractivityRegistrationFormPresetChangedProps = {
  clipId: string;
  action: string;
};

export type VideoManageInteractivityRegistrationFormConnectProvidersProps = {
  clipId: string;
};

export type VideoManageInteractivityRegistrationFormPlacementChangedProps = {
  clipId: string;
  placement: string;
};

export type VideoManageInteractivityRegistrationFormSettingToggledProps = {
  clipId: string;
  setting: string;
  newStatus: boolean;
};

export type VideoManageInteractivityRegistrationFormSectionSwitchedProps = {
  clipId: string;
  section: string;
};

export type VideoManageInteractivityRegistrationFormFieldAddedProps = {
  clipId: string;
  fieldType: string;
};

export type VideoManageInteractivityRegistrationFormHiddenFieldAddedProps = {
  clipId: string;
  fieldType: string;
};

export type VideoManageInteractivityRegistrationFormPrivacyPolicyChangedProps = {
  clipId: string;
};

export type VideoManageInteractivityRegistrationFormAppearanceChangedProps = {
  clipId: string;
  setting: string;
};

export type VideoManageInteractivityRegistrationFormPreviewTypeChangedProps = {
  clipId: string;
  previewType: string;
};

export type VideoManageInteractivityRegistrationFormPreviewDeviceChangedProps = {
  clipId: string;
  device: string;
};

export type VideoManageInteractivityRegistrationRegistrantsActionClickedProps = {
  clipId: string;
  action: string;
};

// =============================================================================
// MAPS
// =============================================================================

const BRANDING_FIELD_MAP: Record<string, string> = {
  colorOne: 'primary_color',
  colorTwo: 'accent_color',
  colorThree: 'icon_text_color',
  colorFour: 'background_color',
  customLogo: 'custom_logo',
};

const ENGAGEMENT_FEATURE_MAP: Record<string, string> = {
  chapters: 'chapters',
  transcript: 'transcript',
  collections: 'add_to_collections',
  download: 'download',
  comments: 'comments',
  like: 'like',
  watchlater: 'watch_later',
  share: 'share',
  embed: 'embed',
  askAi: 'vimeo_ai',
  reaction: 'reaction',
  hideAll: 'hide_all',
};

const DETAIL_FIELD_MAP: Record<string, string> = {
  portrait: 'profile_picture',
  owner: 'byline',
  name: 'name',
  description: 'description',
  views: 'views',
  dateAdded: 'date_added',
  tags: 'tags',
  credits: 'credits',
  categories: 'categories',
  creativeCommons: 'copyright',
  uploader: 'uploaded_by',
  hideAll: 'hide_all',
};

const CONTROL_FIELD_MAP: Record<string, string> = {
  playbar: 'progress_bar',
  volume: 'volume',
  closedCaptions: 'cc_subtitles',
  qualitySelector: 'quality',
  speed: 'speed',
  audioTracks: 'audio_tracks',
  chapters: 'chapters',
  chromecast: 'chromecast',
  airplay: 'airplay',
  pip: 'pip',
  fullscreen: 'fullscreen',
  vimeoLogo: 'vimeo_logo',
  skippingForward: 'skipping_forward',
  playButton_auto: 'play_button_auto',
  playButton_bottom: 'play_button_bottom',
  playButton_center: 'play_button_center',
  transcript: 'transcript',
  hideAll: 'hide_all',
};

export const END_SCREEN_SOURCE_MAP: Record<string, string> = {
  more_videos: 'more_videos',
  call_to_action: 'call_to_action',
  share_options: 'share_buttons',
  video_picture: 'custom_image',
  loop: 'loop',
  back_to_beginning: 'thumbnail',
  empty: 'empty',
  description: 'custom_message',
};

// =============================================================================
// HOOK
// =============================================================================

export const useVideoManageTracking = () => {
  const pico = usePico();

  const trackVideoManagePageDisplayed = useCallback(
    (props: VideoManagePageDisplayedProps) => {
      if (pico === null) return;
      pico.track('video_manage_page_displayed', {
        clip_id: props.clipId,
        referrer_page: props.referrerPage,
      });
    },
    [pico],
  );

  const trackVideoManageStatusChanged = useCallback(
    (props: VideoManageStatusChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_status_changed', {
        clip_id: props.clipId,
        video_manage_new_status: props.newStatus,
      });
    },
    [pico],
  );

  const trackVideoManageOverflowActionClicked = useCallback(
    (props: VideoManageOverflowActionClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_overflow_action_clicked', {
        clip_id: props.clipId,
        video_manage_overflow_action: props.action,
      });
    },
    [pico],
  );

  const trackVideoManageAppearanceDisplayed = useCallback(
    (props: VideoManageAppearanceDisplayedProps) => {
      if (pico === null) return;
      pico.track('video_manage_appearance_displayed', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageAppearanceModeSwitched = useCallback(
    (props: VideoManageAppearanceModeSwitchedProps) => {
      if (pico === null) return;
      const mappedMode = props.mode === 'page' ? 'video_page' : props.mode;
      pico.track('video_manage_appearance_mode_switched', {
        clip_id: props.clipId,
        video_manage_appearance_new_mode: mappedMode,
      });
    },
    [pico],
  );

  const trackVideoManageAppearanceBrandingChanged = useCallback(
    (props: VideoManageAppearanceBrandingChangedProps) => {
      if (pico === null) return;
      const mappedField = BRANDING_FIELD_MAP[props.field];
      if (!mappedField) return;
      const mappedMode = props.mode === 'page' ? 'video_page' : props.mode;
      pico.track('video_manage_appearance_branding_changed', {
        clip_id: props.clipId,
        video_manage_appearance_branding_field: mappedField,
        video_manage_appearance_mode: mappedMode,
      });
    },
    [pico],
  );

  const trackVideoManageAppearanceEngagementToggled = useCallback(
    (props: VideoManageAppearanceEngagementToggledProps) => {
      if (pico === null) return;
      const mappedFeature = ENGAGEMENT_FEATURE_MAP[props.feature];
      if (!mappedFeature) return;
      const mappedMode = props.mode === 'page' ? 'video_page' : props.mode;
      pico.track('video_manage_appearance_engagement_toggled', {
        clip_id: props.clipId,
        video_manage_appearance_engagement_feature: mappedFeature,
        video_manage_appearance_new_status: props.newStatus,
        video_manage_appearance_mode: mappedMode,
      });
    },
    [pico],
  );

  const trackVideoManageAppearanceDetailToggled = useCallback(
    (props: VideoManageAppearanceDetailToggledProps) => {
      if (pico === null) return;
      const mappedField = DETAIL_FIELD_MAP[props.field];
      if (!mappedField) return;
      const mappedMode = props.mode === 'page' ? 'video_page' : props.mode;
      pico.track('video_manage_appearance_detail_toggled', {
        clip_id: props.clipId,
        video_manage_appearance_detail_field: mappedField,
        video_manage_appearance_new_status: props.newStatus,
        video_manage_appearance_mode: mappedMode,
      });
    },
    [pico],
  );

  const trackVideoManageAppearanceSaved = useCallback(
    (props: VideoManageAppearanceSavedProps) => {
      if (pico === null) return;
      const mappedMode = props.mode === 'page' ? 'video_page' : props.mode;
      pico.track('video_manage_appearance_saved', {
        clip_id: props.clipId,
        video_manage_appearance_mode: mappedMode,
      });
    },
    [pico],
  );

  const trackVideoManageAppearanceControlChanged = useCallback(
    (props: VideoManageAppearanceControlChangedProps) => {
      if (pico === null) return;
      const mappedField = CONTROL_FIELD_MAP[props.field];
      if (!mappedField) return;
      const mappedMode = props.mode === 'page' ? 'video_page' : props.mode;
      pico.track('video_manage_appearance_control_changed', {
        clip_id: props.clipId,
        video_manage_appearance_control_field: mappedField,
        video_manage_appearance_mode: mappedMode,
      });
    },
    [pico],
  );

  const trackVideoManageLanguagesDisplayed = useCallback(
    (props: VideoManageLanguagesDisplayedProps) => {
      if (pico === null) return;
      pico.track('video_manage_languages_displayed', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageSubtitlesGenerationStarted = useCallback(
    (props: VideoManageSubtitlesGenerationStartedProps) => {
      if (pico === null) return;
      pico.track('video_manage_subtitles_generation_started', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageSubtitlesUploaded = useCallback(
    (props: VideoManageSubtitlesUploadedProps) => {
      if (pico === null) return;
      pico.track('video_manage_subtitles_uploaded', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageLanguagesAddActionClicked = useCallback(
    (props: VideoManageLanguagesAddActionClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_languages_add_action_clicked', {
        clip_id: props.clipId,
        video_manage_languages_add_action: props.action,
      });
    },
    [pico],
  );

  const trackVideoManageLanguagesOverflowActionClicked = useCallback(
    (props: VideoManageLanguagesOverflowActionClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_languages_overflow_action_clicked', {
        clip_id: props.clipId,
        video_manage_languages_overflow_action: props.action,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityDisplayed = useCallback(
    (props: VideoManageInteractivityDisplayedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_displayed', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityFeatureOpened = useCallback(
    (props: VideoManageInteractivityFeatureOpenedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_feature_opened', {
        clip_id: props.clipId,
        video_manage_interactivity_feature: props.feature,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityChapterAdded = useCallback(
    (props: VideoManageInteractivityChapterAddedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_chapter_added', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityChapterSaved = useCallback(
    (props: VideoManageInteractivityChapterSavedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_chapter_saved', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationToggled = useCallback(
    (props: VideoManageInteractivityRegistrationToggledProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_toggled', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_new_status: props.newStatus,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationCustomizeClicked = useCallback(
    (props: VideoManageInteractivityRegistrationCustomizeClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_customize_clicked', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationManageRegistrantsClicked = useCallback(
    (props: VideoManageInteractivityRegistrationManageRegistrantsClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_manage_registrants_clicked', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityVideoRatingSaved = useCallback(
    (props: VideoManageInteractivityVideoRatingSavedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_video_rating_saved', {
        clip_id: props.clipId,
        video_manage_interactivity_video_rating_type: props.ratingType,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityVideoRatingTypeChanged = useCallback(
    (props: VideoManageInteractivityVideoRatingTypeChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_video_rating_type_changed', {
        clip_id: props.clipId,
        video_manage_interactivity_video_rating_type: props.ratingType,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityCardAdded = useCallback(
    (props: VideoManageInteractivityCardAddedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_card_added', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityCardImageAdded = useCallback(
    (props: VideoManageInteractivityCardImageAddedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_card_image_added', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityEndScreenSourceChanged = useCallback(
    (props: VideoManageInteractivityEndScreenSourceChangedProps) => {
      if (pico === null) return;
      const mappedSource = END_SCREEN_SOURCE_MAP[props.source];
      if (!mappedSource) return;
      pico.track('video_manage_interactivity_end_screen_source_changed', {
        clip_id: props.clipId,
        video_manage_interactivity_end_screen_source: mappedSource,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityCardSaved = useCallback(
    (props: VideoManageInteractivityCardSavedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_card_saved', {
        clip_id: props.clipId,
        video_manage_interactivity_card_has_description: props.hasDescription,
        video_manage_interactivity_card_has_image: props.hasImage,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityEndScreenSaved = useCallback(
    (props: VideoManageInteractivityEndScreenSavedProps) => {
      if (pico === null) return;
      const mappedSource = END_SCREEN_SOURCE_MAP[props.source];
      if (!mappedSource) return;
      if (mappedSource === 'more_videos' && props.videoCount !== undefined) {
        pico.track('video_manage_interactivity_end_screen_saved', {
          clip_id: props.clipId,
          video_manage_interactivity_end_screen_source: mappedSource,
          video_manage_interactivity_end_screen_video_count: props.videoCount,
        });
      } else {
        pico.track('video_manage_interactivity_end_screen_saved', {
          clip_id: props.clipId,
          video_manage_interactivity_end_screen_source: mappedSource,
        });
      }
    },
    [pico],
  );

  const trackVideoManageEditingActionClicked = useCallback(
    (props: VideoManageEditingActionClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_editing_action_clicked', {
        clip_id: props.clipId,
        video_manage_editing_action: props.action,
      });
    },
    [pico],
  );

  const trackVideoManageGifMakerCreateClicked = useCallback(
    (props: VideoManageGifMakerCreateClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_gif_maker_create_clicked', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageGifMakerCreated = useCallback(
    (props: VideoManageGifMakerCreatedProps) => {
      if (pico === null) return;
      pico.track('video_manage_gif_maker_created', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageGifMakerDeleted = useCallback(
    (props: VideoManageGifMakerDeletedProps) => {
      if (pico === null) return;
      pico.track('video_manage_gif_maker_deleted', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageGifMakerShareActionClicked = useCallback(
    (props: VideoManageGifMakerShareActionClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_gif_maker_share_action_clicked', {
        clip_id: props.clipId,
        video_manage_gif_maker_share_action: props.action,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiPanelDisplayed = useCallback(
    (props: VideoManageVimeoAiPanelDisplayedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_panel_displayed', {
        clip_id: props.clipId,
        source: props.source ?? 'sidebar',
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiFeatureClicked = useCallback(
    (props: VideoManageVimeoAiFeatureClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_feature_clicked', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        feature: props.feature,
        location: props.location,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiSettingsClicked = useCallback(
    (props: VideoManageVimeoAiSettingsClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_settings_clicked', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiPanelClosed = useCallback(
    (props: VideoManageVimeoAiPanelClosedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_panel_closed', {
        clip_id: props.clipId,
        close_reason: props.closeReason,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiVideoDetailsShown = useCallback(
    (props: VideoManageVimeoAiVideoDetailsShownProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_video_details_shown', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        fields_generated: props.fieldsGenerated,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiVideoDetailsSaved = useCallback(
    (props: VideoManageVimeoAiVideoDetailsSavedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_video_details_saved', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        field: props.field,
        action: props.action,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiVideoDetailsCopied = useCallback(
    (props: VideoManageVimeoAiVideoDetailsCopiedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_video_details_copied', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        field: props.field,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiChaptersShown = useCallback(
    (props: VideoManageVimeoAiChaptersShownProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_chapters_shown', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        chapter_count: props.chapterCount,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiChaptersChapterClicked = useCallback(
    (props: VideoManageVimeoAiChaptersChapterClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_chapters_chapter_clicked', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        chapter_index: props.chapterIndex,
        timecode: props.timecode,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiChaptersSaved = useCallback(
    (props: VideoManageVimeoAiChaptersSavedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_chapters_saved', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        chapter_count: props.chapterCount,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiHighlightsShown = useCallback(
    (props: VideoManageVimeoAiHighlightsShownProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_highlights_shown', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        highlight_count: props.highlightCount,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiHighlightsHighlightClicked = useCallback(
    (props: VideoManageVimeoAiHighlightsHighlightClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_highlights_highlight_clicked', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        highlight_type: props.highlightType,
        highlight_title: props.highlightTitle,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiHighlightsEdited = useCallback(
    (props: VideoManageVimeoAiHighlightsEditedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_highlights_edited', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        highlight_title: props.highlightTitle,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiHighlightsSaved = useCallback(
    (props: VideoManageVimeoAiHighlightsSavedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_highlights_saved', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        highlight_title: props.highlightTitle,
        highlight_type: props.highlightType,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiHighlightsShared = useCallback(
    (props: VideoManageVimeoAiHighlightsSharedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_highlights_shared', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        distribution_type: props.distributionType,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiAskAiShown = useCallback(
    (props: VideoManageVimeoAiAskAiShownProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_ask_ai_shown', {
        clip_id: props.clipId,
        generation_id: props.generationId,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiAskAiQuestionAsked = useCallback(
    (props: VideoManageVimeoAiAskAiQuestionAskedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_ask_ai_question_asked', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        question: props.question,
        source: props.source,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiAskAiAnswerShown = useCallback(
    (props: VideoManageVimeoAiAskAiAnswerShownProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_ask_ai_answer_shown', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        question: props.question,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiAskAiMomentPlayed = useCallback(
    (props: VideoManageVimeoAiAskAiMomentPlayedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_ask_ai_moment_played', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        question: props.question,
        timecode: props.timecode,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiTranslateSettingsShown = useCallback(
    (props: VideoManageVimeoAiTranslateSettingsShownProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_translate_settings_shown', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        source: props.source,
        product_selection_shown: props.productSelectionShown,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiTranslateSaved = useCallback(
    (props: VideoManageVimeoAiTranslateSavedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_translate_saved', {
        clip_id: props.clipId,
        generation_id: props.generationId,
        source: props.source,
        product_type: props.productType,
        source_language_code: props.sourceLanguageCode,
        target_language_codes: props.targetLanguageCodes,
        language_count: props.languageCount,
        speaker_count: props.speakerCount,
        preview_enabled: props.previewEnabled,
        submit_action: props.submitAction,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiUpsellTriggered = useCallback(
    (props: VideoManageVimeoAiUpsellTriggeredProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_upsell_triggered', {
        clip_id: props.clipId,
        feature: props.feature,
        upsell_name: props.upsellName,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiUpsellImpression = useCallback(
    (props: VideoManageVimeoAiUpsellImpressionProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_upsell_impression', {
        clip_id: props.clipId,
        feature: props.feature,
        upsell_name: props.upsellName,
      });
    },
    [pico],
  );

  const trackVideoManageVimeoAiUpsellContactSalesClicked = useCallback(
    (props: VideoManageVimeoAiUpsellContactSalesClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_vimeo_ai_upsell_contact_sales_clicked', {
        clip_id: props.clipId,
        feature: props.feature,
      });
    },
    [pico],
  );

  const trackVideoManageLanguagesTrackToggled = useCallback(
    (props: VideoManageLanguagesTrackToggledProps) => {
      if (pico === null) return;
      pico.track('video_manage_languages_track_toggled', {
        clip_id: props.clipId,
        track_type: props.trackType,
        language_code: props.languageCode,
        enabled: props.enabled,
      });
    },
    [pico],
  );

  const trackVideoManageLanguagesTrackEdited = useCallback(
    (props: VideoManageLanguagesTrackEditedProps) => {
      if (pico === null) return;
      pico.track('video_manage_languages_track_edited', {
        clip_id: props.clipId,
        track_type: props.trackType,
        language_code: props.languageCode,
      });
    },
    [pico],
  );

  const trackVideoManageLanguagesTrackRetranscribeClicked = useCallback(
    (props: VideoManageLanguagesTrackRetranscribeClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_languages_track_retranscribe_clicked', {
        clip_id: props.clipId,
        track_type: props.trackType,
        language_code: props.languageCode,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormTabSwitched = useCallback(
    (props: VideoManageInteractivityRegistrationFormTabSwitchedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_tab_switched', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_tab: props.tab,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormPresetChanged = useCallback(
    (props: VideoManageInteractivityRegistrationFormPresetChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_preset_changed', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_preset_action: props.action,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormConnectProviders = useCallback(
    (props: VideoManageInteractivityRegistrationFormConnectProvidersProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_connect_providers', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormPlacementChanged = useCallback(
    (props: VideoManageInteractivityRegistrationFormPlacementChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_placement_changed', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_placement: props.placement,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormSettingToggled = useCallback(
    (props: VideoManageInteractivityRegistrationFormSettingToggledProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_setting_toggled', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_setting: props.setting,
        video_manage_interactivity_registration_form_setting_new_status: props.newStatus,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormSectionSwitched = useCallback(
    (props: VideoManageInteractivityRegistrationFormSectionSwitchedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_section_switched', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_section: props.section,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormFieldAdded = useCallback(
    (props: VideoManageInteractivityRegistrationFormFieldAddedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_field_added', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_field_type: props.fieldType,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormHiddenFieldAdded = useCallback(
    (props: VideoManageInteractivityRegistrationFormHiddenFieldAddedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_hidden_field_added', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_hidden_field_type: props.fieldType,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormPrivacyPolicyChanged = useCallback(
    (props: VideoManageInteractivityRegistrationFormPrivacyPolicyChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_privacy_policy_changed', {
        clip_id: props.clipId,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormAppearanceChanged = useCallback(
    (props: VideoManageInteractivityRegistrationFormAppearanceChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_appearance_changed', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_appearance_setting: props.setting,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormPreviewTypeChanged = useCallback(
    (props: VideoManageInteractivityRegistrationFormPreviewTypeChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_preview_type_changed', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_preview_type: props.previewType,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationFormPreviewDeviceChanged = useCallback(
    (props: VideoManageInteractivityRegistrationFormPreviewDeviceChangedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_form_preview_device_changed', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_form_preview_device: props.device,
      });
    },
    [pico],
  );

  const trackVideoManageInteractivityRegistrationRegistrantsActionClicked = useCallback(
    (props: VideoManageInteractivityRegistrationRegistrantsActionClickedProps) => {
      if (pico === null) return;
      pico.track('video_manage_interactivity_registration_registrants_action_clicked', {
        clip_id: props.clipId,
        video_manage_interactivity_registration_registrants_action: props.action,
      });
    },
    [pico],
  );

  return {
    trackVideoManagePageDisplayed,
    trackVideoManageStatusChanged,
    trackVideoManageOverflowActionClicked,
    trackVideoManageAppearanceDisplayed,
    trackVideoManageAppearanceModeSwitched,
    trackVideoManageAppearanceBrandingChanged,
    trackVideoManageAppearanceEngagementToggled,
    trackVideoManageAppearanceDetailToggled,
    trackVideoManageAppearanceSaved,
    trackVideoManageAppearanceControlChanged,
    trackVideoManageLanguagesDisplayed,
    trackVideoManageSubtitlesGenerationStarted,
    trackVideoManageSubtitlesUploaded,
    trackVideoManageLanguagesAddActionClicked,
    trackVideoManageLanguagesOverflowActionClicked,
    trackVideoManageInteractivityDisplayed,
    trackVideoManageInteractivityFeatureOpened,
    trackVideoManageInteractivityChapterAdded,
    trackVideoManageInteractivityChapterSaved,
    trackVideoManageInteractivityRegistrationToggled,
    trackVideoManageInteractivityRegistrationCustomizeClicked,
    trackVideoManageInteractivityRegistrationManageRegistrantsClicked,
    trackVideoManageInteractivityCardAdded,
    trackVideoManageInteractivityCardImageAdded,
    trackVideoManageInteractivityEndScreenSourceChanged,
    trackVideoManageInteractivityVideoRatingSaved,
    trackVideoManageInteractivityVideoRatingTypeChanged,
    trackVideoManageInteractivityCardSaved,
    trackVideoManageInteractivityEndScreenSaved,
    trackVideoManageEditingActionClicked,
    trackVideoManageGifMakerCreateClicked,
    trackVideoManageGifMakerCreated,
    trackVideoManageGifMakerDeleted,
    trackVideoManageGifMakerShareActionClicked,
    trackVideoManageVimeoAiPanelDisplayed,
    trackVideoManageVimeoAiFeatureClicked,
    trackVideoManageVimeoAiSettingsClicked,
    trackVideoManageVimeoAiPanelClosed,
    trackVideoManageVimeoAiVideoDetailsShown,
    trackVideoManageVimeoAiVideoDetailsSaved,
    trackVideoManageVimeoAiVideoDetailsCopied,
    trackVideoManageVimeoAiChaptersShown,
    trackVideoManageVimeoAiChaptersChapterClicked,
    trackVideoManageVimeoAiChaptersSaved,
    trackVideoManageVimeoAiHighlightsShown,
    trackVideoManageVimeoAiHighlightsHighlightClicked,
    trackVideoManageVimeoAiHighlightsEdited,
    trackVideoManageVimeoAiHighlightsSaved,
    trackVideoManageVimeoAiHighlightsShared,
    trackVideoManageVimeoAiAskAiShown,
    trackVideoManageVimeoAiAskAiQuestionAsked,
    trackVideoManageVimeoAiAskAiAnswerShown,
    trackVideoManageVimeoAiAskAiMomentPlayed,
    trackVideoManageVimeoAiTranslateSettingsShown,
    trackVideoManageVimeoAiTranslateSaved,
    trackVideoManageVimeoAiUpsellTriggered,
    trackVideoManageVimeoAiUpsellImpression,
    trackVideoManageVimeoAiUpsellContactSalesClicked,
    trackVideoManageLanguagesTrackToggled,
    trackVideoManageLanguagesTrackEdited,
    trackVideoManageLanguagesTrackRetranscribeClicked,
    trackVideoManageInteractivityRegistrationFormTabSwitched,
    trackVideoManageInteractivityRegistrationFormPresetChanged,
    trackVideoManageInteractivityRegistrationFormConnectProviders,
    trackVideoManageInteractivityRegistrationFormPlacementChanged,
    trackVideoManageInteractivityRegistrationFormSettingToggled,
    trackVideoManageInteractivityRegistrationFormSectionSwitched,
    trackVideoManageInteractivityRegistrationFormFieldAdded,
    trackVideoManageInteractivityRegistrationFormHiddenFieldAdded,
    trackVideoManageInteractivityRegistrationFormPrivacyPolicyChanged,
    trackVideoManageInteractivityRegistrationFormAppearanceChanged,
    trackVideoManageInteractivityRegistrationFormPreviewTypeChanged,
    trackVideoManageInteractivityRegistrationFormPreviewDeviceChanged,
    trackVideoManageInteractivityRegistrationRegistrantsActionClicked,
  };
};
